import { Extension, Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import {
  ExtendedFieldMappingEditorValue,
  transformSimpleValueInput,
} from '@components/text-editors/addons/field-mapping';
import { createPlaceholderHtml, createPlaceholderNode } from '@components/text-editors/helpers';

export const tag = 'token-placeholder';

const attributes = ['value'];

const getPlaceholderHtml = createPlaceholderHtml({ tag, attributes });

const TokenNode = createPlaceholderNode({
  attributes,
  renderText: getPlaceholderHtml,
  tag,
});

export const insertTokenNode = (editor: Editor) => {
  const node = editor.state.selection.$head.nodeBefore;
  const text = node?.textContent ?? '';
  if (node && text.length > 0) {
    const token = text.trim();
    const currentPosition = editor.state.selection.from;
    editor
      .chain()
      .deleteRange({ from: currentPosition - text.length, to: currentPosition })
      .insertContent(getPlaceholderHtml({ value: token }))
      .run();
  }
  return true;
};

const EXTENSION_NAME = 'token';

export const Token = Extension.create({
  name: EXTENSION_NAME,
  tag,
  addExtensions: () => [TokenNode],
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(EXTENSION_NAME),
        props: {
          handleKeyDown: (view, event) => {
            // Handle semicolon specifically by checking the actual character
            // Makes it so icelandic "æ" can be used properly
            if (event.key === ';') {
              insertTokenNode(this.editor);
              return true; // Prevent default behavior
            }
            return false; // Let other keys pass through
          },
        },
      }),
    ];
  },

  addKeyboardShortcuts(this) {
    // ; is also a submit key but we handle that in the prosemirror plugin above
    const submitKeys = ['Enter', 'Tab', ','];
    return submitKeys.reduce(
      (accumulated, key) => ({
        ...accumulated,
        [key]: () => insertTokenNode(this.editor),
      }),
      {},
    );
  },
});

export const transformInput = (input: ExtendedFieldMappingEditorValue[]) =>
  transformSimpleValueInput(input, (value) =>
    getPlaceholderHtml({
      value,
    }),
  );
